import * as RadixAvatar from "@radix-ui/react-avatar";

import { getInitials } from "@/global";
import { css, CssProp } from "@/theme";

/**
 * Test radix component. TODO implement using figma tokens to see how it works
 */

export type AvatarProps = {
  name: string;
  image?: string;
  alt?: string;
  size?: "xsmall" | "small" | "medium" | "default" | "large";
  outlined?: boolean;
  mono?: boolean;
  css?: CssProp;
} & React.RefAttributes<HTMLSpanElement>;

const styles = {
  root: css`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    vertical-align: middle;
    overflow: hidden;
    user-select: none;
    width: 2.5rem;
    height: 2.5rem;
    border-radius: 100%;
    border-width: 1px;

    &[data-size="xsmall"] {
      width: 1rem;
      height: 1rem;
    }

    &[data-size="small"] {
      width: 1.5rem;
      height: 1.5rem;
    }

    &[data-size="medium"] {
      width: 2rem;
      height: 2rem;
    }

    &[data-size="large"] {
      width: 3rem;
      height: 3rem;
    }

    &[data-outlined="true"] {
      border-style: solid;
    }
  `,
  image: css`
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: inherit;

    &[data-mono="true"] {
      // black and white
      filter: grayscale(100%);
    }
  `,
  fallback: css`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: inherit;
    font-size: 15px;
    line-height: 1;
    font-weight: 500;
    border-radius: inherit;
  `
};

export const Avatar = ({
  name,
  image,
  alt,
  size = "default",
  outlined,
  mono,
  css: rcss,
  ...props
}: AvatarProps) => {
  const initials = getInitials(name);
  const rootStyles = rcss ? [styles.root, rcss] : styles.root;

  return (
    <RadixAvatar.Root
      css={rootStyles}
      data-size={size}
      data-outlined={outlined}
      {...props}
    >
      <RadixAvatar.Image
        css={styles.image}
        src={image}
        alt={alt}
        data-mono={mono}
      />
      <RadixAvatar.Fallback css={styles.fallback}>
        {initials}
      </RadixAvatar.Fallback>
    </RadixAvatar.Root>
  );
};
